// Sidebar.js
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaChartBar,
  FaPlus,
  FaBullhorn,
  FaUserFriends,
  FaIdCard,
  FaCog,
  FaBuilding,
  FaShieldAlt,
  FaIndustry,
  FaRecycle,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaCrown,
  FaEdit,
  FaEye,
  FaExchangeAlt,
} from "react-icons/fa";

const Sidebar = ({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => {
  const location = useLocation();
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  
  console.log("Current location in sidebar:", location.pathname);

  const isActive = (path) => {
    // Handle the dashboard case specifically
    if (
      path === "/dashboard" ||
      path === "/" ||
      (path === "" && location.pathname.endsWith("/ai-sdr"))
    ) {
      return (
        location.pathname.endsWith("/dashboard") ||
        location.pathname.endsWith("/ai-sdr") ||
        location.pathname === "/ai-sdr/"
      );
    }
    // Handle other paths
    return location.pathname.includes(path);
  };

  // Helper function to preserve query parameters in navigation links
  const getNavPath = (path) => {
    // Convert internal path to full AI-SDR path and preserve query params
    const fullPath = path === "/" ? "/ai-sdr/" : `/ai-sdr${path}`;
    const finalPath = `${fullPath}${location.search}`;
    console.log(
      "getNavPath:",
      path,
      "->",
      finalPath,
      "search:",
      location.search
    );
    return finalPath;
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case "owner":
        return <FaCrown className="text-yellow-500" />;
      case "creator":
        return <FaEdit className="text-green-500" />;
      case "reader":
        return <FaEye className="text-blue-500" />;
      default:
        return null;
    }
  };

  // Get role label
  const getRoleLabel = (role) => {
    switch (role) {
      case "owner":
        return "Owner";
      case "creator":
        return "Creator";
      case "reader":
        return "Reader";
      default:
        return "Unknown";
    }
  };

  // Handle team switching
  const handleTeamSwitch = (teamId) => {
    if (switchToTeam) {
      switchToTeam(teamId);
    }
    setShowTeamDropdown(false);
  };

  const menuItems = [
    {
      path: "/",
      label: "Dashboard",
      icon: <FaChartBar />,
      description: "Overview and stats",
    },
    {
      path: "/campaign-builder",
      label: "Campaign Builder",
      icon: <FaPlus />,
      description: "Create and edit campaigns",
    },
    {
      path: "/campaigns",
      label: "Campaigns",
      icon: <FaBullhorn />,
      description: "Manage all campaigns",
    },
    {
      path: "/leads",
      label: "Leads",
      icon: <FaUserFriends />,
      description: "Track and manage sales leads",
    },
    {
      path: "/personas",
      label: "Personas",
      icon: <FaIdCard />,
      description: "Manage sender personas",
    },
    {
      path: "/companies",
      label: "Companies",
      icon: <FaBuilding />,
      description: "Manage company records and data",
    },
    {
      path: "/research",
      label: "Research Viewer",
      icon: <FaSearch />,
      description: "View enriched company research data",
    },
    {
      path: "/generators",
      label: "Generators",
      icon: <FaIndustry />,
      description: "Manage generator sites and find similar facilities",
    },
    {
      path: "/collectors",
      label: "Collectors",
      icon: <FaRecycle />,
      description: "Browse and search Reworld facilities",
    },
    {
      path: "/blacklist-checker",
      label: "Blacklist Checker",
      icon: <FaShieldAlt />,
      description: "Verify email addresses against blacklists",
    },
    {
      path: "/settings",
      label: "Settings",
      icon: <FaCog />,
      description: "Team and account settings",
    },
  ];

  return (
    <aside className="w-64 bg-white shadow-lg flex flex-col h-screen sticky top-0 border-r border-purple-100">
      {/* Logo Header */}
      <div className="p-6 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-violet-50">
        <Link to={getNavPath("/")}>
          <div className="flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mr-3">
              <FaChartBar className="text-white text-lg" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              AI-SDR
            </h1>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-3">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={getNavPath(item.path)}
              className={`
                flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group relative
                ${
                  isActive(item.path)
                    ? "bg-gradient-to-r from-purple-100 to-violet-100 text-purple-700 border border-purple-200 shadow-sm"
                    : "text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                }
              `}
            >
              <span
                className={`mr-3 transition-colors ${
                  isActive(item.path)
                    ? "text-purple-600"
                    : "text-gray-400 group-hover:text-purple-500"
                }`}
              >
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              
              {/* Active indicator */}
              {isActive(item.path) && (
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-purple-500 to-violet-500 rounded-l-full"></div>
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Footer with Team Information */}
      <div className="border-t border-purple-100 bg-gradient-to-r from-purple-50 to-violet-50">
        {/* Enhanced Team Information with Dropdown */}
        {selectedTeam && (
          <div className="bg-white mx-3 my-3 rounded-lg border border-purple-200 shadow-sm">
            {/* Current Team Display */}
            <div className="p-3">
              <div className="text-xs text-gray-600 font-medium mb-2">
                Active Team:
              </div>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-purple-700 text-sm truncate">
                    {selectedTeam.name}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {getRoleIcon(selectedTeam.role)}
                    <span className="text-xs text-gray-600">
                      {getRoleLabel(selectedTeam.role)}
                    </span>
                  </div>
                </div>
                
                {/* Team Switcher Button */}
                {teams && teams.length > 1 && (
                  <button
                    onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                    className="ml-2 p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors"
                    title="Switch Team"
                  >
                    {showTeamDropdown ? (
                      <FaChevronUp className="text-xs" />
                    ) : (
                      <FaChevronDown className="text-xs" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Team Dropdown */}
            {showTeamDropdown && teams && teams.length > 1 && (
              <div className="border-t border-purple-100 max-h-48 overflow-y-auto">
                <div className="p-2">
                  <div className="text-xs text-gray-500 font-medium px-2 py-1 mb-2">
                    Switch to team:
                  </div>
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => handleTeamSwitch(team.id)}
                      disabled={team.id === selectedTeam.id}
                      className={`w-full text-left p-2 rounded-md transition-colors mb-1 ${
                        team.id === selectedTeam.id
                          ? "bg-purple-100 border border-purple-300 cursor-default"
                          : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-xs truncate ${
                            team.id === selectedTeam.id
                              ? "text-purple-800"
                              : "text-gray-900"
                          }`}>
                            {team.name}
                            {team.id === selectedTeam.id && (
                              <span className="ml-1 text-xs text-purple-600">
                                (Current)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            {getRoleIcon(team.role)}
                            <span className="text-xs text-gray-500">
                              {getRoleLabel(team.role)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Additional Options */}
                <div className="border-t border-purple-100 p-2">
                  <button
                    onClick={() => {
                      setShowTeamDropdown(false);
                      if (clearTeamSelection) clearTeamSelection();
                    }}
                    className="w-full text-left p-2 rounded-md hover:bg-gray-50 text-xs text-gray-600 flex items-center gap-2"
                  >
                    <FaExchangeAlt />
                    Show team selection screen
                  </button>
                </div>
              </div>
            )}

            {/* Fallback for single team or no switcher */}
            {teams && teams.length === 1 && (
              <div className="px-3 pb-2">
                <div className="text-xs text-gray-500">
                  You have access to 1 team
                </div>
              </div>
            )}

            {/* Legacy Switch Team Button (kept for compatibility) */}
            {teams && teams.length > 1 && !showTeamDropdown && (
              <div className="px-3 pb-2 border-t border-purple-100 pt-2">
                <button
                  onClick={clearTeamSelection}
                  className="text-xs text-purple-600 hover:text-purple-800 underline transition-colors flex items-center gap-1"
                >
                  <FaExchangeAlt className="text-xs" />
                  Team selection screen
                </button>
              </div>
            )}
          </div>
        )}

        {/* App Info */}
        <div className="p-4 text-center">
          <p className="text-xs text-gray-500">
            AI-SDR Platform
          </p>
          <p className="text-xs text-purple-600 font-medium">
            Enhanced Team Management
          </p>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
